import React from 'react';
import { Brain, Cpu, Database, Sparkles } from 'lucide-react';

interface Props {
  /** Compact eyebrow shown on the form card header. */
  eyebrow: string;
  /** Instrument-Serif card title. */
  title: string;
  /** One-line description under the title. */
  subtitle: string;
  /** Icon shown inside the bordered square at the header right. */
  icon: React.ReactNode;
  /** Coloured accent on the header icon (used for OTP-style steps). */
  iconAccent?: boolean;
  /** Optional eyebrow on the LEFT brand panel (defaults to RatibaEdu). */
  brandPillLabel?: string;
  /** Optional one-line headline for the LEFT brand panel. */
  brandHeadline?: React.ReactNode;
  /** Optional sub for the LEFT brand panel. */
  brandSub?: string;
  /** Centre form contents. */
  children: React.ReactNode;
  /** Optional card footer row (links, registration prompt). */
  footer?: React.ReactNode;
}

/**
 * Two-panel auth shell mirroring the kisiwa_management_dash login.
 *
 * Left panel (desktop only): brand colour, decorative circles, RatibaEdu
 * pill, headline + sub, AI illustration, three stat pills. Mobile
 * collapses to a brand header above the card. Pure Tailwind with a
 * single inline <style> block for the subtle grid pattern + a float
 * keyframe.
 */
export const AuthShell: React.FC<Props> = ({
  eyebrow,
  title,
  subtitle,
  icon,
  iconAccent,
  brandPillLabel = 'RatibaEdu',
  brandHeadline,
  brandSub,
  children,
  footer,
}) => {
  return (
    <div
      className="lg-root relative flex min-h-screen overflow-hidden bg-[#f0f4f8]"
      style={{ fontFamily: "'Afacad Flux', system-ui, sans-serif" }}
    >
      <style>{`
        .lg-root::before {
          content: '';
          position: fixed; inset: 0; pointer-events: none; z-index: 0;
          background-image:
            linear-gradient(rgba(26,82,118,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(26,82,118,0.04) 1px, transparent 1px);
          background-size: 40px 40px;
        }
        @keyframes lgFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
        .lg-float { animation: lgFloat 4.5s ease-in-out infinite; }
      `}</style>

      {/* ── Left brand panel (desktop only) ── */}
      <aside className="relative z-10 hidden w-[45%] flex-col items-center justify-center overflow-hidden bg-[#1a5276] px-12 py-16 lg:flex">
        {/* Decorative circles + soft blur orb */}
        <div className="pointer-events-none absolute -left-20 -top-20 h-80 w-80 rounded-full border border-white/10" />
        <div className="pointer-events-none absolute -bottom-32 -right-32 h-[480px] w-[480px] rounded-full border border-white/5" />
        <div className="pointer-events-none absolute bottom-[20%] left-[10%] h-52 w-52 rounded-full bg-[#0e9172]/20 blur-3xl" />

        {/* Brand pill */}
        <div className="relative z-10 mb-12 inline-flex items-center gap-2.5 rounded-full border border-white/15 bg-white/10 px-5 py-2 text-[13px] font-semibold uppercase tracking-[0.06em] text-white/90">
          <span
            className="inline-flex h-[22px] w-[22px] items-center justify-center rounded-full bg-white text-[14px] text-[#1a5276]"
            style={{ fontFamily: "'Instrument Serif', serif" }}
          >
            R
          </span>
          {brandPillLabel}
        </div>

        {/* Headline */}
        <h1
          className="relative z-10 mb-4 text-center text-[40px] font-normal leading-[1.15] text-white"
          style={{ fontFamily: "'Instrument Serif', serif" }}
        >
          {brandHeadline ?? (
            <>
              Teach the model with<br />
              <em className="not-italic italic text-[#d4f0e9]/90">your past papers</em>
            </>
          )}
        </h1>

        <p className="relative z-10 mb-14 max-w-[320px] text-center text-[15px] leading-relaxed text-white/60">
          {brandSub ??
            "Upload CDACC papers, internal exams and rubrics — your self-hosted Llama learns the institution's house style."}
        </p>

        {/* AI illustration */}
        <div className="relative z-10 h-[260px] w-[200px]">
          <div
            className="absolute bottom-0 left-2 h-[160px] w-[58px] origin-bottom-left rotate-[-8deg] rounded-lg bg-gradient-to-b from-[#2980b9] to-[#1a5276] shadow-[0_20px_40px_rgba(0,0,0,0.3)]"
            style={{ zIndex: 2 }}
          />
          <div
            className="absolute bottom-0 right-2 h-[150px] w-[58px] origin-bottom-right rotate-[8deg] rounded-lg bg-gradient-to-b from-[#0e9172] to-[#085041] shadow-[0_20px_40px_rgba(0,0,0,0.3)]"
            style={{ zIndex: 2 }}
          />
          <div
            className="absolute bottom-3 left-6 h-[100px] w-[48px] rotate-[-3deg] rounded-lg bg-gradient-to-b from-[#8e44ad] to-[#5b2c6f] shadow-[0_18px_36px_rgba(0,0,0,0.28)]"
            style={{ zIndex: 1 }}
          />
          <div
            className="lg-float absolute bottom-0 left-1/2 flex h-[200px] w-[72px] -translate-x-1/2 flex-col items-center justify-center gap-3 rounded-[10px] bg-gradient-to-b from-[#0f1923] to-[#1a5276] shadow-[0_22px_44px_rgba(0,0,0,0.35)]"
            style={{ zIndex: 3 }}
          >
            <span className="absolute inset-x-0 top-0 h-[40%] rounded-t-[10px] bg-white/5" />
            <Brain size={28} className="text-[#5ecdb0]" />
            <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-white/70">AI</span>
            <Sparkles size={14} className="text-[#5ecdb0]/70" />
          </div>
        </div>

        {/* Stat pills */}
        <div className="relative z-10 mt-12 flex gap-3">
          <StatPill icon={<Database size={11} />} num="100%"  label="Self-hosted" />
          <StatPill icon={<Cpu size={11} />}      num="Llama" label="8B model" />
          <StatPill icon={<Sparkles size={11} />} num="RAG"   label="Grounded" />
        </div>
      </aside>

      {/* ── Right form panel ── */}
      <main className="relative z-10 flex flex-1 items-center justify-center px-6 py-10 sm:px-10 sm:py-14">
        <div className="w-full max-w-[440px]">
          {/* Mobile-only brand header */}
          <div className="mb-8 text-center lg:hidden">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-[#0f1923] px-5 py-2 text-[13px] font-semibold uppercase tracking-[0.06em] text-white">
              <span
                className="inline-flex h-[22px] w-[22px] items-center justify-center rounded-full bg-white text-[14px] text-[#0f1923]"
                style={{ fontFamily: "'Instrument Serif', serif" }}
              >
                R
              </span>
              {brandPillLabel}
            </div>
            <h1
              className="mb-1.5 text-[28px] font-normal text-slate-900"
              style={{ fontFamily: "'Instrument Serif', serif" }}
            >
              Trainer <em className="italic text-[#1a5276]">portal</em>
            </h1>
            <p className="m-0 text-[14px] text-slate-500">
              Upload past papers to teach the AI
            </p>
          </div>

          {/* Card */}
          <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white">
            <div className="flex items-start justify-between border-b border-slate-100 px-7 pb-6 pt-7 sm:px-8">
              <div className="min-w-0">
                <div className="mb-1 text-[11px] font-bold uppercase tracking-[0.1em] text-[#1a5276]">
                  {eyebrow}
                </div>
                <h2
                  className="m-0 mb-1 text-[24px] font-normal leading-tight text-slate-900"
                  style={{ fontFamily: "'Instrument Serif', serif" }}
                >
                  {title}
                </h2>
                <p className="m-0 text-[13.5px] text-slate-500">{subtitle}</p>
              </div>
              <div
                className={`
                  flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-[10px]
                  border border-slate-200 bg-slate-50
                  ${iconAccent ? 'text-[#0e9172]' : 'text-[#1a5276]'}
                `}
              >
                {icon}
              </div>
            </div>

            <div className="px-7 py-7 sm:px-8">{children}</div>

            {footer && (
              <div className="border-t border-slate-100 px-7 py-5 text-center sm:px-8">
                {footer}
              </div>
            )}
          </div>

          <p className="mt-6 text-center text-[12px] text-slate-400">
            © {new Date().getFullYear()} Mutable Investment Limited · AI Trainer Portal
          </p>
        </div>
      </main>
    </div>
  );
};

/* ── Local atoms ─────────────────────────────────────────────── */
const StatPill: React.FC<{ icon: React.ReactNode; num: string; label: string }> = ({ icon, num, label }) => (
  <div className="rounded-full border border-white/[0.12] bg-white/[0.08] px-4 py-2.5 text-center">
    <span
      className="block text-[18px] leading-tight text-white"
      style={{ fontFamily: "'Instrument Serif', serif" }}
    >
      <span className="mr-1 inline-flex align-middle text-[#5ecdb0]">{icon}</span>
      {num}
    </span>
    <span className="block text-[11px] font-medium uppercase tracking-[0.04em] text-white/50">
      {label}
    </span>
  </div>
);
